import React from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getLocale, alignTimezone } from '../../../helpers/dates';
import { formatMoney } from '../../../helpers/format';
import { useTranslation } from 'react-i18next';
import styles from './statusTracker.module.css';
import ValidityIcon from '../../icons/ValidityIcon';
import CategoryStarIcon from '../../icons/CategoryStarIcon';
import PointsIcon from '../../icons/PointsIcon';

const StatusTracker = ({ title, points, categories, categoryName }) => {
  const { t } = useTranslation();
  const categoryId = useSelector((state) => state.associates.user.salesCategory.categoryId);
  const app = useSelector((state) => state.app);
  const locale = getLocale(app.language);
  const qualifyBy = useSelector((state) => state.associates.user.salesCategory.fromDate);
  const pointsNextCategory = useSelector(
    (state) => state.associates.user.salesCategory.pointsNextCategory,
  );
  const qualifyFinish = useSelector(
    (state) => state.associates.user.salesCategory?.nextCategorizationDate,
  );
  const nextCategoryName = useSelector(
    (state) => state.associates.user.salesCategory?.nextCategory,
  );
  const commissionPercentage = useSelector(
    (state) => state.associates.user.salesCategory?.commissionPercentage,
  );
  const affiliateType = useSelector((state) => state.associates.user.affiliateType);

  const qualifyByFormatted = format(alignTimezone(qualifyBy), 'MMMM d, yyyy', { locale });
  const qualifyFinishByFormatted = qualifyFinish
    ? format(alignTimezone(qualifyFinish), 'MMMM d, yyyy', { locale })
    : null;

  const qualifyFinishByFormattedmonth = qualifyFinish
    ? format(alignTimezone(qualifyFinish), 'MMMM, yyyy', { locale })
    : null;

  const currentCategoryIndex = categories.findIndex((category) => category.id === categoryId);
  const nextCategory = categories[currentCategoryIndex - 1];

  const pointsNeeded = nextCategory ? pointsNextCategory - points : 0;
  const progressPercentage = nextCategory
    ? Math.min((points / pointsNextCategory) * 100, 100)
    : 100;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className='font-normal fw-bold mb-1'>{title}</div>
          <div className='font-smaller text-medium-gray'>
            {t('ratedSince')} <span className={styles.capitalize}>{qualifyByFormatted}</span>
          </div>
        </div>

        {qualifyFinishByFormatted && (
          <div className={styles.validityCard}>
            <div className={styles.validityIcon}>
              <ValidityIcon size={20} />
            </div>
            <div className={styles.validityContent}>
              <div className={`${styles.validityLabel} font-extra-small fw-semibold`}>
                {t('categoryValidUntil')}
              </div>
              <div className={`sentences-capitalize font-1rem fw-bold`}>
                {qualifyFinishByFormatted}
              </div>
            </div>
            <div className={styles.validityGlow} />
          </div>
        )}

        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <CategoryStarIcon size={24} />
            </div>
            <div className={` mb-1 text-medium-gray  font-extra-small semi-bold`}>
              {t('currentCategory')}
            </div>
            <div className={` font-large fw-bold`}>{categoryName}</div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <PointsIcon size={24} />
            </div>
            <div className={` mb-1 text-medium-gray font-extra-small semi-bold`}>
              {t('totalPoints')}
            </div>
            <div className={`font-large fw-bold`}>
              {formatMoney(points, { decimals: 0, currencySymbol: '' }, true)}
            </div>
          </div>
        </div>
      </div>

      {affiliateType === 'INTERNAL_STAFF' && (
        <div className={`${styles.container} mt-2 mb-2`}>
          <div className={styles.commissionCard}>
            <div className='text-lighter-gray semi-bold font-1rem mb-2'>{t('yourCommission')}</div>
            <div className={styles.commissionValue}>
              <span className={`${styles.percentage} font-extra-large fw-extrabold`}>
                {commissionPercentage}
              </span>
              <span className={`text-primary font-extra-large fw-bold`}>%</span>
            </div>
            <div className='text-lighter-gray semi-bold font-1rem'>{t('onFinalConsumption')}</div>
          </div>
        </div>
      )}

      {qualifyFinishByFormatted && (
        <div className={`${styles.container} mt-2`}>
          <div className={styles.header}>
            <div className='font-normal fw-bold'>
              {affiliateType === 'CONCIERGE' ? (
                <>
                  {t('qualificationPointsSince')} {qualifyFinishByFormattedmonth}
                </>
              ) : (
                <>{t('qualificationPoints')}</>
              )}
            </div>
          </div>

          {!nextCategory ? (
            <div className={styles.maxLevel}>
              <div className={styles.trophyIcon}>
                <div className={styles.trophyGlow} />
                🏆
              </div>
              <div className={`mb-1 font-semi-large fw-bold`}>
                {t('category')} {categoryName}
              </div>
              <div className={`text-lighter-gray  font-tiny`}>{t('maxLevelReached')}</div>
            </div>
          ) : (
            <div className={`${styles.progressSection} mt-4`}>
              <div className={styles.nextLevelBadge}>
                <span className={styles.nextLevelIcon}>🎯</span>
                <span className='font-tiny fw-semibold'>
                  {t('pointsForNextLevel', { level: nextCategory.name })}
                </span>
              </div>

              <div className={styles.progressHeader}>
                <div className={`${styles.progressStats} font-1rem fw-bold`}>
                  <span className={styles.currentPoints}>
                    {formatMoney(points, { decimals: 0, currencySymbol: '' }, true)}
                  </span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.targetPoints}>
                    {formatMoney(pointsNextCategory, { decimals: 0, currencySymbol: '' }, true)}
                  </span>
                </div>
              </div>

              <div className={styles.progressBarWrapper}>
                <div className={styles.progressBarContainer}>
                  <div
                    className={styles.progressBarFill}
                    style={{ width: `${progressPercentage}%` }}
                  >
                    <div className={styles.progressShine} />
                  </div>
                  <div
                    className={styles.progressGlow}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <div className={`${styles.progressPercentage} font-normal fw-bold`}>
                  {Math.floor(progressPercentage)}%
                </div>
              </div>

              <div className={`${styles.pointsNeeded} text-lighter-gray  font-tiny`}>
                {t('youNeed')}{' '}
                <span className={` text-primary font-1rem fw-bold`}>
                  {formatMoney(
                    Math.max(pointsNeeded, 0),
                    { decimals: 0, currencySymbol: '' },
                    true,
                  )}
                </span>{' '}
                {t('morePointsforLevel')}{' '}
                <span className={` text-primary font-1rem fw-bold`}>{nextCategoryName}.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default StatusTracker;
